// This file is synced with the supabase edge functions folder to resolve Deno
// being unable to handle the zod import.
// Function request and response schemas should be defined here at @libs/schemas/src/appSchemas.ts
//  and they will be automatically copied to @apps/supabase/supabase/functions/_shared/schemas/appSchemas.ts
//  by the file-sync VSCode extension.
import { z } from "zod";

import {
  insertEnrolmentsTypeSchema,
  insertStaffTypeSchema,
  insertStudentsTypeSchema,
  publicEnrolmentStatusEnumSchema,
  publicPaymentStatusEnumSchema,
  publicPaymentTypeEnumSchema,
  publicStaffRoleEnumSchema,
  publicStudentStatusEnumSchema,
  rowEnrolmentsDocumentsTypesTypeSchema,
  rowEnrolmentsProductsTypeSchema,
} from "./dbSchemas.gen.ts";
import { i18n } from "./i18nUtils.ts";

const HTTPStatusSuccessCodes = {
  OK: 200 as const,
  CREATED: 201 as const,
  NO_CONTENT: 204 as const,
};

const HTTPStatusErrorCodes = {
  BAD_REQUEST: 400 as const,
  UNAUTHORIZED: 401 as const,
  FORBIDDEN: 403 as const,
  NOT_FOUND: 404 as const,
  INTERNAL_SERVER_ERROR: 500 as const,
};

export const HTTPStatusCode = {
  ...HTTPStatusSuccessCodes,
  ...HTTPStatusErrorCodes,
};

export type HTTPStatusErrorCodeType =
  (typeof HTTPStatusErrorCodes)[keyof typeof HTTPStatusErrorCodes];

export type HttpStatusCodeType =
  (typeof HTTPStatusCode)[keyof typeof HTTPStatusCode];

export type ApiResponseBodyType = {
  message?: string;
  [key: string]: unknown;
};

export type ApiResponseParamsType<T> = {
  body: ApiResponseBodyType;
} & (
  | {
      statusCode: HttpStatusCodeType;
      responseSchema?: never;
    }
  | {
      responseSchema: z.ZodType<T>;
      statusCode?: never;
    }
);

// Common
export const requiredStringSchema = z
  .string(i18n.t("common:validation.requiredField"))
  .trim()
  .min(1, i18n.t("common:validation.requiredField"));

export const requiredEmailSchema = requiredStringSchema.email({
  message: i18n.t("common:validation.invalidEmail"),
});

export const requiredPhoneNumberSchema = requiredStringSchema
  .trim()
  .length(10, i18n.t("common:validation.phoneNumber10Digits"));

// -----|FORMS AND FUNCTIONS SCHEMAS|-----
export const staffFormSchema = insertStaffTypeSchema
  .omit({
    id: true,
    created_at: true,
    updated_at: true,
    full_text_search: true,
  })
  .extend({
    first_name: requiredStringSchema,
    last_name: requiredStringSchema,
    email: requiredEmailSchema,
    role: publicStaffRoleEnumSchema.refine(
      val => val !== undefined && val !== null,
      { message: i18n.t("common:validation.roleIsRequired") },
    ),
    campus_ids: z.array(z.string().uuid()).nullish(),
  });

export type StaffFormType = z.infer<typeof staffFormSchema>;

export const createStaffSchema = staffFormSchema.superRefine((val, ctx) => {
  if (
    val.role === "CAMPUS_MANAGER" &&
    !(Array.isArray(val.campus_ids) && val.campus_ids.length)
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["campus_ids"],
      message: i18n.t("main:staff.form.errors.atLeastOneCampusRequired"),
    });
  }
});

export type CreateStaffType = z.infer<typeof createStaffSchema>;

export const updateStaffSchema = staffFormSchema
  .extend({
    id: z.string().uuid(),
  })
  .superRefine((val, ctx) => {
    if (
      val.role === "CAMPUS_MANAGER" &&
      !(Array.isArray(val.campus_ids) && val.campus_ids.length)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["campus_ids"],
        message: i18n.t("main:staff.form.errors.atLeastOneCampusRequired"),
      });
    }
  });
export type UpdateStaffType = z.infer<typeof updateStaffSchema>;

export const studentFormSchema = insertStudentsTypeSchema
  .extend({
    email: requiredEmailSchema,
    first_name: requiredStringSchema,
    last_name: requiredStringSchema,
    phone_number: requiredPhoneNumberSchema,
    status: publicStudentStatusEnumSchema,
    send_invitation: z.boolean().default(false),
  })
  .omit({
    id: true,
    created_at: true,
    updated_at: true,
    student_number: true, // student_number is auto-generated
  });

export type StudentFormType = z.infer<typeof studentFormSchema>;

export const createStudentSchema = studentFormSchema;
export type CreateStudentType = z.infer<typeof createStudentSchema>;

export const updateStudentSchema = studentFormSchema
  .omit({
    send_invitation: true,
  })
  .extend({
    id: z.string().uuid(),
  });
export type UpdateStudentType = z.infer<typeof updateStudentSchema>;

const enrolmentsProductSchema = rowEnrolmentsProductsTypeSchema
  .omit({
    created_at: true,
    updated_at: true,
    id: true,
    enrolment_id: true,
  })
  .extend({
    id: z.string().uuid().optional(),
  });

export type EnrolmentsProductType = z.infer<typeof enrolmentsProductSchema>;

export const enrolmentsDocumentsTypesSchema =
  rowEnrolmentsDocumentsTypesTypeSchema
    .omit({
      created_at: true,
      updated_at: true,
      enrolment_id: true,
    })
    .extend({
      id: z.string().uuid().nullish(),
    });

export type EnrolmentsDocumentsTypesType = z.infer<
  typeof enrolmentsDocumentsTypesSchema
>;

export const registrationFormSchema = z.object({
  student_id: z
    .string()
    .uuid(i18n.t("main:registrations.form.errors.studentRequired")),
  campus_id: z
    .string()
    .uuid(i18n.t("main:registrations.form.errors.campusRequired")),
  course_id: z.string(i18n.t("main:registrations.form.errors.courseRequired")),
  start_date: z
    .string()
    .datetime(i18n.t("main:registrations.form.errors.startDateRequired")),
  end_date: z
    .string()
    .datetime(i18n.t("main:registrations.form.errors.endDateRequired")),
  has_advanced_standing: z.boolean().default(false),
  advanced_standing_details: z.string().optional().nullable(),
  status: publicEnrolmentStatusEnumSchema.default("PENDING"),
  enrolments_products: z
    .array(enrolmentsProductSchema)
    .min(1, {
      message: i18n.t(
        "main:registrations.form.errors.atLeastOneProductRequired",
      ),
    })
    .default([]),
  enrolments_documents_types: z
    .array(enrolmentsDocumentsTypesSchema)
    .default([]),
});

export type RegistrationFormType = z.infer<typeof registrationFormSchema>;

export const createRegistrationSchema = insertEnrolmentsTypeSchema
  .merge(registrationFormSchema)
  .omit({
    id: true,
    created_at: true,
    updated_at: true,
  })
  .superRefine((data, ctx) => {
    if (
      data.has_advanced_standing &&
      (!data.advanced_standing_details ||
        data.advanced_standing_details.trim() === "")
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t("common:validation.requiredField"),
        path: ["advanced_standing_details"],
      });
    }
  });

export type CreateRegistrationType = z.infer<typeof createRegistrationSchema>;

export const calculateEnrolmentProgressSchema = z.object({
  enrolmentId: z.string().uuid(),
});

export type CalculateEnrolmentProgressType = z.infer<
  typeof calculateEnrolmentProgressSchema
>;

export const calculateAllEnrolmentProgressSchema = z.object({
  enrolmentIds: z.array(z.string().uuid()),
});

export type CalculateAllEnrolmentProgressType = z.infer<
  typeof calculateAllEnrolmentProgressSchema
>;

export const publishEventSchema = z.object({
  id: z.string().uuid(),
});

export type PublishEventType = z.infer<typeof publishEventSchema>;

export const publishEventsSchema = z.object({
  eventIds: z.array(z.string().uuid()),
});

export type PublishEventsType = z.infer<typeof publishEventsSchema>;

export const processEventEmailJobsSchema = z.object({
  eventId: z.string().uuid(),
  enrolmentId: z.string().uuid().nullish(),
  staffId: z.string().uuid().nullish(),
});

export type ProcessEventEmailJobsType = z.infer<
  typeof processEventEmailJobsSchema
>;

export const cancelEventSchema = z.object({
  id: z.string().uuid(),
  is_published: z.boolean().default(false),
});

export type CancelEventType = z.infer<typeof cancelEventSchema>;

export const skillEvaluationSchema = z.object({
  evaluation_id: z.string().uuid().optional(),
  skill_id: z.string().uuid(),
  grade: z.number().int().min(1).max(5).nullable(),
});

export type SkillEvaluationType = z.infer<typeof skillEvaluationSchema>;

export const upsertBatchEvaluationsSchema = z.object({
  event_id: z.string().uuid(),
  event_notes: z.string().nullish(),
  skills_evaluations: z.array(skillEvaluationSchema).nullish(),
});

export type UpsertBatchEvaluationsType = z.infer<
  typeof upsertBatchEvaluationsSchema
>;

export const createInvoiceItemSchema = z.object({
  enrolment_product_id: z.string().uuid(),
  amount: z.number().nonnegative(),
});

export const createInvoicePaymentScheduleSchema = z.object({
  due_date: z.string(),
  total: z.number().nonnegative(),
  notes: z.string().nullish(),
});

export const createInvoiceSchema = z
  .object({
    enrolment_id: z.string().uuid(),
    payer_id: z.string().uuid(),
    due_date: z.string(),
    items: z.array(createInvoiceItemSchema).min(1),
    payment_schedule: z.array(createInvoicePaymentScheduleSchema).min(1),
  })
  .refine(
    d =>
      Math.abs(
        d.items.reduce((s, i) => s + i.amount, 0) -
          d.payment_schedule.reduce((s, p) => s + p.total, 0),
      ) <= 0.005,
    {
      message: i18n.t(
        "main:registrations.details.invoiceDrafts.form.errors.scheduleMismatch",
      ),
      path: ["payment_schedule"],
    },
  );

export type CreateInvoiceType = z.infer<typeof createInvoiceSchema>;

export const updateInvoiceSchema = z
  .object({
    invoice_id: z.string().uuid(),
    payer_id: z.string().uuid(),
    due_date: z.string(),
    items: z.array(createInvoiceItemSchema).min(1),
    payment_schedule: z.array(createInvoicePaymentScheduleSchema).min(1),
  })
  .refine(
    d =>
      Math.abs(
        d.items.reduce((s, i) => s + i.amount, 0) -
          d.payment_schedule.reduce((s, p) => s + p.total, 0),
      ) <= 0.005,
    {
      message: i18n.t(
        "main:registrations.details.invoiceDrafts.form.errors.scheduleMismatch",
      ),
      path: ["payment_schedule"],
    },
  );

export type UpdateInvoiceType = z.infer<typeof updateInvoiceSchema>;

export const createPaymentSchema = z.object({
  payer_id: z.string().uuid().nullish(),
  invoice_id: z.string().uuid().nullish(),
  date: z.string(),
  enrolment_id: z.string().uuid(),
  amount: z.number(),
  type: publicPaymentTypeEnumSchema,
  note: z.string().nullish(),
});

export type CreatePaymentType = z.infer<typeof createPaymentSchema>;

export const updatePaymentSchema = z.object({
  payment_id: z.string().uuid(),
  date: z.string().optional(),
  amount: z.number().optional(),
  note: z.string().nullish(),
  status: publicPaymentStatusEnumSchema.optional(),
});

export type UpdatePaymentType = z.infer<typeof updatePaymentSchema>;

export const unwrapStudentDEKSchema = z.object({
  target_student_ids: z.string().uuid().array().nullish(),
});

export type UnwrapStudentDEKType = z.infer<typeof unwrapStudentDEKSchema>;

export const backfillSINEncryptionSchema = z.object({
  batch_size: z.number().int().min(1).max(100).optional().default(25),
});

export type BackfillSINEncryptionType = z.infer<
  typeof backfillSINEncryptionSchema
>;

// -----|FUNCTIONS RESPONSE SCHEMAS|-----
export const healthCheckResponseSchema = z.object({
  message: z.literal("ok"),
});
export type HealthCheckResponseType = z.infer<typeof healthCheckResponseSchema>;

export const creationResponseSchema = z.object({
  id: z.string().uuid(),
});

export type CreationResponseType = z.infer<typeof creationResponseSchema>;

export const createUserResponseSchema = z
  .object({
    first_name: z.string(),
    last_name: z.string(),
  })
  .extend(creationResponseSchema.shape);

export type CreateUserResponseType = z.infer<typeof createUserResponseSchema>;
export type UpdateUserResponseType = z.infer<typeof createUserResponseSchema>;

export const batchCreationResponseSchema = z.object({
  message: z.string(),
  ids: z.array(z.string().uuid()),
});

export type BatchCreationResponseType = z.infer<
  typeof batchCreationResponseSchema
>;

export const updatePaymentResponseSchema = z
  .object({
    enrolment_id: z.string().uuid(),
  })
  .extend(creationResponseSchema.shape);

export type UpdatePaymentResponseType = z.infer<
  typeof updatePaymentResponseSchema
>;

export const processEventEmailJobsResponseSchema = z
  .object({
    message: z.string(),
  })
  .extend(creationResponseSchema.shape);

export type ProcessEventEmailJobsResponseType = z.infer<
  typeof processEventEmailJobsResponseSchema
>;

export type CalculateEnrolmentProgressResponseType = z.infer<
  typeof creationResponseSchema
>;

export type CalculateAllEnrolmentProgressResponseType = z.infer<
  typeof batchCreationResponseSchema
>;

export const unwrapStudentDEKResponseSchema = z.object({
  deks: z.array(
    z.object({
      student_id: z.string().uuid(),
      dek: z.string(),
    }),
  ),
});

export type UnwrapStudentDEKResponseType = z.infer<
  typeof unwrapStudentDEKResponseSchema
>;

export const backfillSINEncryptionResponseSchema = z.object({
  processed: z.number(),
  total_remaining: z.number(),
});

export type BackfillSINEncryptionResponseType = z.infer<
  typeof backfillSINEncryptionResponseSchema
>;

// -----|EDGE FUNCTIONS|-----
export enum EdgeFunctionsEnum {
  HealthCheck = "health-check",
  CancelEvent = "cancel-event",
  CreateStaff = "create-staff",
  CreateStudent = "create-student",
  CreateEnrolment = "create-enrolment",
  CalculateEnrolmentProgress = "calculate-enrolment-progress",
  CalculateAllEnrolmentProgress = "calculate-all-enrolment-progress",
  PublishEvent = "publish-event",
  PublishEvents = "publish-events",
  ProcessEventEmailJobs = "process-event-email-jobs",
  UpdateStaff = "update-staff",
  UpdateStudent = "update-student",
  UpsertBatchEvaluations = "upsert-batch-evaluations",
  CreatePayment = "create-payment",
  UpdatePayment = "update-payment",
  RefreshCalendarEventsMV = "refresh-calendar-events-mv",
  UnwrapStudentDEK = "unwrap-student-dek",
  BackfillSINEncryption = "backfill-sin-encryption",
  CreateInvoice = "create-invoice",
  UpdateInvoice = "update-invoice",
}

export type EdgeFunctionOptionsType = {
  [EdgeFunctionsEnum.HealthCheck]: never;
  [EdgeFunctionsEnum.CancelEvent]: {
    body: CancelEventType;
  };
  [EdgeFunctionsEnum.CreateStaff]: {
    body: CreateStaffType;
  };
  [EdgeFunctionsEnum.CreateStudent]: {
    body: CreateStudentType;
  };
  [EdgeFunctionsEnum.UpdateStaff]: {
    body: UpdateStaffType;
  };
  [EdgeFunctionsEnum.UpdateStudent]: {
    body: UpdateStudentType;
  };
  [EdgeFunctionsEnum.CreateEnrolment]: {
    body: CreateRegistrationType;
  };
  [EdgeFunctionsEnum.CalculateEnrolmentProgress]: {
    body: CalculateEnrolmentProgressType;
  };
  [EdgeFunctionsEnum.CalculateAllEnrolmentProgress]: {
    body: CalculateAllEnrolmentProgressType;
  };
  [EdgeFunctionsEnum.PublishEvent]: {
    body: PublishEventType;
  };
  [EdgeFunctionsEnum.PublishEvents]: {
    body: PublishEventsType;
  };
  [EdgeFunctionsEnum.UpsertBatchEvaluations]: {
    body: UpsertBatchEvaluationsType;
  };
  [EdgeFunctionsEnum.CreatePayment]: {
    body: CreatePaymentType;
  };
  [EdgeFunctionsEnum.UpdatePayment]: {
    body: UpdatePaymentType;
  };
  [EdgeFunctionsEnum.CreateInvoice]: {
    body: CreateInvoiceType;
  };
  [EdgeFunctionsEnum.UpdateInvoice]: {
    body: UpdateInvoiceType;
  };
  [EdgeFunctionsEnum.ProcessEventEmailJobs]: {
    body: ProcessEventEmailJobsType;
  };
  [EdgeFunctionsEnum.RefreshCalendarEventsMV]: never;
  [EdgeFunctionsEnum.UnwrapStudentDEK]: {
    body: UnwrapStudentDEKType;
  };
  [EdgeFunctionsEnum.BackfillSINEncryption]: {
    body: BackfillSINEncryptionType;
  };
};

export type EdgeFunctionReturnType = {
  [EdgeFunctionsEnum.HealthCheck]: HealthCheckResponseType;
  [EdgeFunctionsEnum.CancelEvent]: CreationResponseType;
  [EdgeFunctionsEnum.CreateStaff]: CreateUserResponseType;
  [EdgeFunctionsEnum.CreateStudent]: CreateUserResponseType;
  [EdgeFunctionsEnum.UpdateStaff]: UpdateUserResponseType;
  [EdgeFunctionsEnum.UpdateStudent]: UpdateUserResponseType;
  [EdgeFunctionsEnum.CreateEnrolment]: CreationResponseType;
  [EdgeFunctionsEnum.PublishEvent]: CreationResponseType;
  [EdgeFunctionsEnum.PublishEvents]: CreationResponseType;
  [EdgeFunctionsEnum.ProcessEventEmailJobs]: ProcessEventEmailJobsResponseType;
  [EdgeFunctionsEnum.UpsertBatchEvaluations]: BatchCreationResponseType;
  [EdgeFunctionsEnum.CreatePayment]: CreationResponseType;
  [EdgeFunctionsEnum.UpdatePayment]: UpdatePaymentResponseType;
  [EdgeFunctionsEnum.CreateInvoice]: CreationResponseType;
  [EdgeFunctionsEnum.UpdateInvoice]: CreationResponseType;
  [EdgeFunctionsEnum.CalculateEnrolmentProgress]: CalculateEnrolmentProgressResponseType;
  [EdgeFunctionsEnum.CalculateAllEnrolmentProgress]: CalculateAllEnrolmentProgressResponseType;
  [EdgeFunctionsEnum.RefreshCalendarEventsMV]: void;
  [EdgeFunctionsEnum.UnwrapStudentDEK]: UnwrapStudentDEKResponseType;
  [EdgeFunctionsEnum.BackfillSINEncryption]: BackfillSINEncryptionResponseType;
};
